import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Check, SpinnerGap, Crown, ArrowRight, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, error, free
  const sessionId = searchParams.get('session_id');
  const isFree = searchParams.get('free') === '1';

  useEffect(() => {
    if (isFree) {
      setStatus('free');
      // Clean up any legacy provisional password data
      try { sessionStorage.removeItem('habla_prov_pwd'); } catch (e) { /* noop */ }
      return;
    }
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isFree]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    if (attempts >= maxAttempts) { setStatus('error'); return; }
    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Zahlung erfolgreich! Willkommen bei Premium!');
        // Meta Pixel — Purchase event with real value/currency from the backend.
        try {
          const { trackEvent } = await import('../utils/metaPixel');
          const value = Number(res.data.amount || 0);
          const currency = String(res.data.currency || 'eur').toUpperCase();
          trackEvent('Purchase', {
            value: value || undefined,
            currency,
            content_ids: res.data.package_id ? [res.data.package_id] : undefined,
            content_type: 'product',
          });
          trackEvent('Subscribe', { value: value || undefined, currency });
        } catch { /* pixel disabled */ }
        return;
      } else if (res.data.status === 'expired') { setStatus('error'); return; }
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen canvas-spain flex items-center justify-center p-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
            <SpinnerGap size={48} className="animate-spin text-[#AB1C24] mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-[#292524] mb-2">Zahlung wird verarbeitet...</h1>
            <p className="text-[#57534E]">Bitte warte einen Moment, während wir deine Zahlung bestätigen.</p>
          </div>
        )}

        {status === 'free' && (
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" weight="bold" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#292524] mb-2">Zugang freigeschaltet!</h1>
            <p className="text-[#57534E] mb-6">
              Willkommen bei Habla. Dein Konto wurde erstellt — melde dich jetzt mit deiner E-Mail und deinem gewählten Passwort an.
            </p>
            <Button onClick={() => navigate('/login')} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="go-to-login-btn">
              Jetzt anmelden
              <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} className="text-green-600" weight="bold" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#292524] mb-2">¡Felicidades!</h1>
            <p className="text-[#57534E] mb-6">
              Deine Zahlung war erfolgreich. Du hast jetzt vollen Zugang zu allen Premium-Funktionen!
            </p>
            <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 mb-6">
              <Crown size={32} className="text-amber-500 mx-auto mb-2" weight="fill" />
              <span className="font-heading font-bold text-amber-700">Premium aktiviert</span>
            </div>
            <Button onClick={() => navigate('/dashboard')} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]" data-testid="go-to-dashboard-btn">
              Zum Dashboard <ArrowRight size={20} className="ml-2" />
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <X size={40} className="text-red-500" weight="bold" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#292524] mb-2">Etwas ist schief gelaufen</h1>
            <p className="text-[#57534E] mb-6">
              Die Zahlung konnte nicht bestätigt werden. Bitte kontaktiere uns, wenn du bereits bezahlt hast.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/profile')} className="w-full bg-[#AB1C24] hover:bg-[#8A151C]">
                Zurück zum Profil
              </Button>
              <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                Erneut prüfen
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
